/// <amd-module name="Artifacts/FeedSettings/containers/PermissionsTabContainer" />

import { PermissionsTab } from "Artifacts/FeedSettings/components/PermissionsTab";
import { FeedSettingsActions, IFeedSettingsModuleState } from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { GenericModalId, IGenericModalProps } from "Artifacts/Root/components/GenericModal";
import { ComponentState } from "Artifacts/Root/Constants";
import { isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import { isUserFeedAdmin } from "Artifacts/Root/helpers/UserHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { RootActions } from "Artifacts/Root/Redux/Actions";
import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { IRoutedTabContainerProps } from "Artifacts/Root/types/IRoutedTabContainerProps";
import { FeedPermission } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { IFeedSettings } from "Artifacts/FeedSettings/types";

type IPermissionsTabContainerProps = IRoutedTabContainerProps &
  IPermissionsTabMapStateToProps &
  typeof Actions;

interface IPermissionsTabMapStateToProps {
  componentState: ComponentState;
  permissions?: FeedPermission[];
  addPermissionComponentState: ComponentState;
  deletePermissionComponentState: ComponentState;
  settings?: IFeedSettings;
}

const Actions = {
  loadPermissions: FeedSettingsActions.loadPermissions,
  addPermission: FeedSettingsActions.addPermission,
  deletePermission: FeedSettingsActions.deletePermission,
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

function mapStateToProps(state: IFeedSettingsModuleState): IPermissionsTabMapStateToProps {
  const hubState = state["feed-settings"].hubState;
  return {
    componentState: hubState.permissionsComponentState,
    permissions: hubState.permissions,
    addPermissionComponentState: hubState.addPermissionComponentState,
    deletePermissionComponentState: hubState.deletePermissionComponentState,
    settings: hubState.settings
  };
}

const PermissionsTabContainer: React.FC<IPermissionsTabContainerProps> = ({
  permissions,
  componentState,
  loadPermissions,
  addPermission,
  addPermissionComponentState,
  deletePermission,
  deletePermissionComponentState,
  showModal,
  hideModal,
  settings
}) => {
  const { feed } = useFeed();
  const isFeedDeleted = feed === undefined ? true : feed.isDeleted;
  React.useEffect(() => {
    feed && loadPermissions(feed);
  }, [loadPermissions, feed]);

  const [showAddPermissionPanel, setShowAddPermissionPanel] = useState<boolean>(false);
  const dismissAddPermissionPanel = () => {
    setShowAddPermissionPanel(false);
  };

  const showDeleteModal = (identityToDelete: string, setFocus: () => void) => {
    const permissionToDelete = permissions?.find((p) => p.identityId === identityToDelete);
    if (permissionToDelete) {
      const confirmDeleteModal: ICurrentModal<IGenericModalProps> = {
        modalId: GenericModalId,
        props: {
          title: Res.FeedSettings_Permissions_Delete_Title,
          primaryButtonText: Res.Delete_Label,
          primaryButtonDanger: true,
          body: Res.FeedSettings_Permissions_Delete_Content,
          onPrimaryClicked: () =>
            feed && deletePermission(feed, permissionToDelete, permissions || [], hideModal),
          onDismiss: () => {
            hideModal();
            setFocus();
          },
          primaryButtonComponentState: deletePermissionComponentState,
        },
      };
      showModal(confirmDeleteModal, "deletePermissionModal");
    }
  };

  return (
    <PermissionsTab
      permissions={permissions}
      componentState={componentState}
      addPermission={(permission) =>
        feed && addPermission(feed, permission, permissions || [], dismissAddPermissionPanel)
      }
      addPermissionComponentState={addPermissionComponentState}
      showDeleteModal={showDeleteModal}
      setShowAddPermissionPanel={setShowAddPermissionPanel}
      showAddPermissionPanel={showAddPermissionPanel}
      isFeedDeleted={isFeedDeleted}
      isPublicFeed={feed ? isPublicFeed(feed) : false}
      isUserAdmin={feed !== undefined && isUserFeedAdmin(feed)}
      supportServicePrincipals={settings?.supportServicePrincipals ?? false}
    />
  );
};

const ConnectedPermissionsTabContainer = connect<
  IPermissionsTabMapStateToProps,
  unknown,
  IRoutedTabContainerProps
>(
  mapStateToProps,
  Actions,
)(PermissionsTabContainer);

export {
  ConnectedPermissionsTabContainer as PermissionsTabContainer,
  IPermissionsTabContainerProps,
};
